import { CommonModule } from '@angular/common';
import { injectUrlViewState } from '../../../utils/url-view-state.util';
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { Subscription } from 'rxjs';
import { ToastService } from '../../../services/toast.service';
import { NotificationService } from '../../../services/notification.service';
import {
  INBOX_CATEGORIES,
  InboxCategory,
  InboxNotification,
  categoryForEvent,
  isAlertNotification,
} from '../../../models/notification';
import {
  dashboardRoute,
  formatDayHeader,
  formatMoment,
  formatRelative,
  parseCreatedAt,
  routeActionLabel,
} from '../../../utils/notification-format.util';

/** One chip on the filter strip. */
export interface InboxFilter {
  label: string;
  /** Null on All and Unread. */
  categoryKey: string | null;
  unreadOnly: boolean;
}

/** A row with everything the template needs already worked out. */
interface InboxRow {
  notification: InboxNotification;
  category: InboxCategory;
  time: string;
  isAlert: boolean;
}

/** A day band and the rows under it. */
interface InboxGroup {
  label: string;
  rows: InboxRow[];
}

const PAGE_SIZE = 20;

/** How many extra pages a narrow chip may pull in before it settles. */
const MAX_AUTO_PAGES = 3;

/**
 * Settings → Notifications. Figma "Settings notifications" (Admin-Dashboard
 * 357:5258): the inbox card with its filter chips and day-grouped rows, and
 * the detail rail a row opens.
 *
 * The service has no filter parameter, so the chips narrow the pages already
 * loaded and a narrow chip asks for more pages on its own.
 */
@Component({
  selector: 'app-notifications',
  standalone: true,
  imports: [CommonModule, RouterLink],
  templateUrl: './notifications.component.html',
  styleUrl: './notifications.component.css',
})
export class NotificationsComponent implements OnInit, OnDestroy {
  private readonly notifications = inject(NotificationService);
  private readonly router = inject(Router);
  private readonly toast = inject(ToastService);

  readonly filters: InboxFilter[] = [
    { label: 'All', categoryKey: null, unreadOnly: false },
    { label: 'Unread', categoryKey: null, unreadOnly: true },
    ...INBOX_CATEGORIES.map((c) => ({
      label: c.label,
      categoryKey: c.key,
      unreadOnly: false,
    })),
  ];

  static readonly loadingMoreText = 'Loading more notifications…';
  static readonly markAllFailed = "Couldn't mark everything as read";
  static readonly readFailed = "Couldn't update that notification";

  selectedFilter: InboxFilter = this.filters[0];
  /** The filter chip lives in the URL (?filter=unread|<category key>), so a
   * browser reload reopens the same inbox view. */
  private urlState = injectUrlViewState();

  /** A chip's URL value: 'unread', its category key, or null for All. */
  private filterKey(filter: InboxFilter): string | null {
    if (filter.unreadOnly) return 'unread';
    return filter.categoryKey;
  }

  loading = true;
  loadError = false;
  loadingMore = false;
  loadMoreError = false;
  menuOpen = false;

  items: InboxNotification[] = [];
  private cursor: string | null = null;

  /** The row the detail rail is showing, if any. */
  selected: InboxNotification | null = null;

  private countWatch?: Subscription;

  ngOnInit(): void {
    const fromUrl = this.urlState.get('filter');
    this.selectedFilter =
      this.filters.find((f) => fromUrl !== null && this.filterKey(f) === fromUrl) ??
      this.filters[0];
    this.load();
    // The badge is polled service-side. A number above what this list knows
    // about means something arrived while the page was open, so fetch the
    // newest page and slot the new rows in.
    this.countWatch = this.notifications.unreadCount$.subscribe((serverUnread) => {
      if (!this.loading && !this.loadingMore && serverUnread > this.unreadCount) {
        this.pullLatest();
      }
    });
  }

  ngOnDestroy(): void {
    this.countWatch?.unsubscribe();
  }

  /**
   * Fetches page one and inserts anything this list has not seen. Rows already
   * on screen are left exactly as they are, so a row the reader just opened
   * does not flick back to unread when a slower reply lands.
   */
  private pullLatest(): void {
    if (this.loadingMore) return;
    this.loadingMore = true;
    this.notifications.getMessages(PAGE_SIZE).subscribe({
      next: (page) => {
        const seen = new Set(this.items.map((n) => n.notification_id));
        const fresh = (page.items ?? []).filter((n) => !seen.has(n.notification_id));
        if (fresh.length > 0) this.items = [...fresh, ...this.items];
        this.loadingMore = false;
      },
      error: () => {
        this.loadingMore = false;
      },
    });
  }

  // ------------------------------------------------------------------ load

  load(): void {
    this.loading = true;
    this.loadError = false;
    this.notifications.getMessages(PAGE_SIZE).subscribe({
      next: (page) => {
        this.items = page.items ?? [];
        this.cursor = page.last_evaluated_key ?? null;
        this.loading = false;
        this.notifications.unreadCount$.next(page.unread_count ?? this.unreadCount);
        this.fillForFilter();
      },
      error: () => {
        this.loading = false;
        this.loadError = true;
      },
    });
  }

  /** Appends the next page. Ignored while one is in flight or the list ends. */
  loadMore(): void {
    if (this.loadingMore || !this.hasMore) return;
    this.loadingMore = true;
    this.loadMoreError = false;
    this.notifications.getMessages(PAGE_SIZE, this.cursor).subscribe({
      next: (page) => {
        const seen = new Set(this.items.map((n) => n.notification_id));
        // A row written while paging can repeat across pages; appending it
        // twice would break the list.
        this.items = [
          ...this.items,
          ...(page.items ?? []).filter((n) => !seen.has(n.notification_id)),
        ];
        this.cursor = page.last_evaluated_key ?? null;
        this.loadingMore = false;
      },
      error: () => {
        this.loadingMore = false;
        this.loadMoreError = true;
      },
    });
  }

  onScroll(event: Event): void {
    const el = event.target as HTMLElement;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 240) {
      this.loadMore();
    }
  }

  /**
   * Pulls pages until the chip has something to show or the cap is hit. A chip
   * that matches nothing on page one would otherwise say "No results" while
   * the rows it wants sit on page two.
   */
  private fillForFilter(attempt = 0): void {
    if (attempt >= MAX_AUTO_PAGES) return;
    if (this.visibleCount >= PAGE_SIZE / 2 || !this.hasMore || this.loadingMore) return;
    this.loadingMore = true;
    this.notifications.getMessages(PAGE_SIZE, this.cursor).subscribe({
      next: (page) => {
        const seen = new Set(this.items.map((n) => n.notification_id));
        this.items = [
          ...this.items,
          ...(page.items ?? []).filter((n) => !seen.has(n.notification_id)),
        ];
        this.cursor = page.last_evaluated_key ?? null;
        this.loadingMore = false;
        this.fillForFilter(attempt + 1);
      },
      error: () => {
        this.loadingMore = false;
        this.loadMoreError = true;
      },
    });
  }

  // --------------------------------------------------------------- derived

  get hasMore(): boolean {
    return !!this.cursor;
  }

  get unreadCount(): number {
    return this.items.filter((n) => !n.is_read).length;
  }

  get unreadLabel(): string {
    const count = this.unreadCount;
    return `${count > 99 ? '99+' : count} unread`;
  }

  get visible(): InboxNotification[] {
    const filter = this.selectedFilter;
    return this.items.filter((n) => {
      if (filter.unreadOnly && n.is_read) return false;
      if (filter.categoryKey && categoryForEvent(n.metadata?.event).key !== filter.categoryKey) {
        return false;
      }
      return true;
    });
  }

  get visibleCount(): number {
    return this.visible.length;
  }

  /** The rows the list draws, banded by day. */
  get groups(): InboxGroup[] {
    const now = new Date();
    const groups: InboxGroup[] = [];
    for (const notification of this.visible) {
      const at = parseCreatedAt(notification.created_at);
      const label = at ? formatDayHeader(at, now) : 'Earlier';
      const row: InboxRow = {
        notification,
        category: categoryForEvent(notification.metadata?.event),
        time: at ? formatRelative(at, now) : '',
        isAlert: isAlertNotification(notification),
      };
      const last = groups[groups.length - 1];
      if (last && last.label === label) {
        last.rows.push(row);
      } else {
        groups.push({ label, rows: [row] });
      }
    }
    return groups;
  }

  get emptyTitle(): string {
    return this.selectedFilter === this.filters[0] ? "You're all caught up" : 'No results';
  }

  get emptyMessage(): string {
    if (this.selectedFilter === this.filters[0]) {
      return 'New alerts about payments, orders and your team will show here.';
    }
    const what = this.selectedFilter.unreadOnly ? 'unread' : this.selectedFilter.label;
    return this.hasMore
      ? `No ${what} notifications in what's loaded yet. Show all, or keep scrolling.`
      : `There are no ${what} notifications yet. They'll appear here when they arrive.`;
  }

  // ------------------------------------------------------------ detail rail

  get selectedCategory(): InboxCategory | null {
    return this.selected ? categoryForEvent(this.selected.metadata?.event) : null;
  }

  get selectedMoment(): string {
    const at = this.selected ? parseCreatedAt(this.selected.created_at) : null;
    return at ? formatMoment(at) : '';
  }

  get selectedRoute(): string | null {
    return this.selected ? dashboardRoute(this.selected.metadata?.payload) : null;
  }

  get selectedActionLabel(): string | null {
    return routeActionLabel(this.selectedRoute);
  }

  // ---------------------------------------------------------------- actions

  selectFilter(filter: InboxFilter): void {
    this.selectedFilter = filter;
    this.urlState.set('filter', this.filterKey(filter));
    this.fillForFilter();
  }

  showAll(): void {
    this.selectFilter(this.filters[0]);
  }

  /** A row opens the rail and, if it was unread, marks it read. */
  open(notification: InboxNotification): void {
    this.selected = notification;
    this.menuOpen = false;
    if (notification.is_read) return;
    this.setRead(notification, true);
  }

  closeDetail(): void {
    this.selected = null;
  }

  isSelected(notification: InboxNotification): boolean {
    return this.selected?.notification_id === notification.notification_id;
  }

  openRoute(): void {
    const route = this.selectedRoute;
    if (route) this.router.navigateByUrl(route);
  }

  toggleRead(): void {
    if (!this.selected) return;
    this.setRead(this.selected, !this.selected.is_read);
  }

  markAllRead(): void {
    this.menuOpen = false;
    const unread = this.items.filter((n) => !n.is_read);
    if (unread.length === 0) return;
    unread.forEach((n) => (n.is_read = true));
    this.notifications.markAllRead().subscribe({
      next: () => this.notifications.refreshCounts(),
      error: () => {
        unread.forEach((n) => (n.is_read = false));
        this.toast.error('Nothing was changed.', NotificationsComponent.markAllFailed);
      },
    });
  }

  /** Read state flips on screen first and is put back if the service refuses. */
  private setRead(notification: InboxNotification, read: boolean): void {
    notification.is_read = read;
    const request = read
      ? this.notifications.markRead(notification.notification_id)
      : this.notifications.markUnread(notification.notification_id);
    request.subscribe({
      next: () => this.notifications.refreshCounts(),
      error: () => {
        notification.is_read = !read;
        this.toast.error('Nothing was changed.', NotificationsComponent.readFailed);
      },
    });
  }
}
